import { routerNames } from "@/components/appCommon.constants";
import { splitAndConcatFromCapitalString } from "@/common/utils/utils";
import { createFormDataForSelectedFiles } from "@/common/utils/fileUploader";

export const manageChapterStudioConstant = {
    PAGE_TITLE: "Chapter Studio",
    PAGE_SUBTITLE: "Manage Announcements",
    CREATE_ACTION_BUTTON_LABEL: "Create Announcement",
    DISPLAY_RANGE: "Display Range: ",
    EFFECTIVE_RANGE: "Effective Range: ",
    ANNOUNCEMENT_TYPE: "Announcement Type: ",
    DEPT_REGION: "Dept/Region: ",
    VISIBLE_TO: "Visible To: ",
    SHORT_DESCRIPTION: "Short Description:",
    UNSCHEDULED_DRAFT: "UNSCHEDULED DRAFT",
    EDIT: "Edit",
    DELETE: "Delete",
    DELETE_CONFIRMATION_MESSAGE: "Are you sure you want to delete the Announcement?",
    ITEM_DELETED_SUCCESS: "Announcement Deleted Successfully",
    ITEM_FAILED_DELETE: "Error, Delete Failed!",
    MAX_LIMIT_TEXT_REGIONS: "You can only select up to 4 regions.",
    PAST_ANNOUNCEMENT: "Past Announcements (Last 90 days)",
};

export const createChapterStudioConstant = {
    PAGE_TITLE: "Chapter Studio",
    PAGE_SUBTITLE: "Create Announcement",
    CANCEL_BUTTON_LABEL: "Cancel",
    EDIT_ANNOUNCEMENT_BREADCRUMB: 'Edit Announcement',
    RADIO_COLOR: '#0057B7',
    IMAGE: 'Image',
    ENTER_LOCATION: 'Enter the Location',
    ENTER_PREVIEW_DESCRIPTION: 'Enter preview description',
    ENTER_TITLE: 'Enter the Title',
    ENTER_CTA_LINK: 'Enter CTA Link',
    SPONSOR_REQUIRED_MESSAGE: '*Department OR Region required',
    PREVIEW_TITLE: 'Announcement Preview',
    PUBLISH_REQUIRED_DISCLAIMER: "*Please fill required fields to enable PUBLISH",
    START_DATE: "Start date",
    END_DATE: "End date",
    ERROR_MESSAGE: {
        END_DATE: "End date must be after start date!",
        CHARACTER_LIMIT: "Character limit exceeded",
        PUBLISHED_SUCCESS: "Announcement Published Successfully",
        DRAFT_SUCCESS: "Announcement saved to Draft successfully.",
        ANNOUNCEMENT_FAILURE: "Something went wrong, please try again later."
    },
    SUCCESS_MESSAGE: {
        PUBLISHED_SUCCESS: "Announcement Published Successfully",
        DRAFT_SUCCESS: "Announcement saved to Draft successfully.",
    },
    RECORD_TYPE: {
        WARNING: 'Warning',
        IMAGE: 'Image',
        GENERAL: 'General',
    },
    ANNOUNCEMENT_IMAGE_TYPE: {
      IMAGE: 'Upload Image',
      BACKGROUND_COLOR: 'Background Color',
    },
    SECTIONS: {
        TYPE: 'Announcement Type*',
        SPONSOR: 'Sponsor*',
        TITLE: 'Announcement Title*',
        SHORT_DESC: 'Preview Description*',
        LONG_DESC: 'Modal Message (optional)',
        SCHEDULER: 'Schedule Announcement*',
        DEPARTMENTS: 'YPO Department*',
        REGIONS: 'YPO Region*',
        VISIBLE_TO: 'Visible to*',
    },
    MAX_TEXT_LIMIT: {
        SHORT_DESC_LIMIT: 120,
        TITLE_LIMIT: 30,
        LOCATION_LIMIT: 120,
        LONG_DESC_LIMIT: 1000,
    },
    FORM_FIELD: {
        TYPE_IMAGE: "Image Announcement",
        TYPE_WARNING: "Warning Announcement",
        TYPE_GENERAL: "General Announcement",
        DISPLAY_RANGE: 'Display Range*',
        EFFECTIVE_RANGE: 'Effective Range (optional)',
        CTA_LINK: 'CTA Link*',
        LOCATION_OPTIONAL: 'Location (optional)',
        SELECT_IMAGE_TITLE: "Select Image*",
        SELECT_BACKGROUND_TITLE: "Select Background Color*",
        SELECT_IMAGE_TITLE_SUB: "(dynamically populated)",
        SELECT_IMAGE_GUIDANCE: "Optimal image specifications: JPG, PNG 620x450",
    },
    ACTION: {
        UPLOAD: 'Upload',
        SAVE_DRAFT: 'Save as Draft',
        PREVIEW: 'Preview',
        PUBLISH: 'Publish',
        BACK_TO_EDIT: "Back To Edit",
    },
};

export const getBreadcrumbFragmentFromRouteName = (routeData, isDisabled) => {
    const { name, path } = routeData;
    return {
        key: name || 'Home',
        text: splitAndConcatFromCapitalString(name),
        href: `/#${path || `/${routerNames.HOME}`}`,
        disabled: !!isDisabled,
    };
};

export const generateDefaultBreadcrumbsForManageChapterStudio = (routeData) => {
    const result = [
        getBreadcrumbFragmentFromRouteName({
            name: routerNames.MANAGE_CHAPTER_STUDIO,
            path: `/${routerNames.MANAGE_CHAPTER_STUDIO}`,
        }),
    ];

    if (routeData && routeData.name) {
        result.unshift(getBreadcrumbFragmentFromRouteName(routeData));
    }
    return result;
};

export const generateManageChapterStudioBreadcrumbForCreateFlow = (breadcrumbList) => {
    let result = [];
    const createAnnouncementRoute = getBreadcrumbFragmentFromRouteName({
        name: routerNames.CREATE_ANNOUNCEMENT,
        path: `/${routerNames.CREATE_ANNOUNCEMENT}`,
    });

    if (breadcrumbList && breadcrumbList.length) {
        const breadcrumbAlreadyPresent = breadcrumbList.some(crumb => crumb.key && crumb.key === createAnnouncementRoute.key);
        if (breadcrumbAlreadyPresent) {
            result = breadcrumbList.filter(crumb => crumb.key !== createAnnouncementRoute.key);
        } else {
            result = [
                ...breadcrumbList,
                createAnnouncementRoute,
            ];
        }
    }
    return result;
};

export const getDefaultPicklist = () => {
    return {
        departments: [],
        regions: [],
        visibleTo: [],
        backgroundColors: [
            "#9E794D",
            "#257FC3",
            "#0D2A45",
        ],
    };
};

export const getDefaultAnnouncementObj = () => {
    return {
        files: null,
        title: '',
        sponsorRegionsList: [],
        backgroundColor: null,
        backgroundImage: null,
        recordTypeName: createChapterStudioConstant.RECORD_TYPE.WARNING,
        location: '',
        displayStartDate: '',
        displayEndDate: '',
        effectiveStartDate: '',
        effectiveEndDate: '',
        shortDescription: '',
        longDescription: '',
        isActive: true,
        isPublished: false,
        visibleToObject: null,
        ctaLink:'',
    };
};

export const announcementSections = (isMobile) => {
    const defaultVal = isMobile ? 1 : 0;
    return {
        type: defaultVal,
        sponsor: defaultVal,
        title: defaultVal,
        shortDescription: defaultVal,
        longDescription: defaultVal,
        scheduler: defaultVal,
    };
};

export function createAnnouncementFormData(announcement) {
    let formData = new FormData();
    if (announcement.files && announcement.files.length > 0) {
      formData = createFormDataForSelectedFiles(announcement.files);
    } else if (announcement.backgroundColor) {
      formData.append("backgroundColor", announcement.backgroundColor);
    } else if (announcement.backgroundImage) {
      formData.append("backgroundImage", announcement.backgroundImage);
    }  
    formData.append("id", announcement.id || "");
    formData.append("ctaLink", announcement.ctaLink);
    formData.append("displayStartDate", announcement.displayStartDate);
    formData.append("displayEndDate", announcement.displayEndDate);
    formData.append("shortDescription", announcement.shortDescription);
    formData.append("longDescription", announcement.longDescription);
    formData.append("visibleToObject", JSON.stringify(announcement.visibleToObject));
    formData.append("title", announcement.title);
    formData.append("sponsorDepartmentList", JSON.stringify(announcement.sponsorDepartmentList));
    formData.append("sponsorRegionsList", JSON.stringify(announcement.sponsorRegionsList));
    formData.append("recordTypeName", announcement.recordTypeName);
    formData.append("effectiveStartDate", announcement.effectiveStartDate);
    formData.append("effectiveEndDate", announcement.effectiveEndDate);
    formData.append("isActive", true);
    formData.append("location", announcement.location);
    formData.append("isPublished", announcement.isPublished);    
    
    return formData;
  }
  
